import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule, FormBuilder, Validators, AbstractControl } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ParentsInformationService } from '../../../services/parents-information/parents-information.service';
import { isFieldInvalid } from '../../shared/validators/form-validation.utils';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { forkJoin, Observable, of } from 'rxjs';
import { FieldValidators } from '../../shared/validators/field-validators';
import { FieldErrorComponent } from '../../shared/components/field-error/field-error.component';

export const parentValidator: import('@angular/forms').ValidatorFn = (control: AbstractControl): import('@angular/forms').ValidationErrors | null => {
  const group = control as FormGroup;
  const fName = group.get('fatherName');
  const fMob = group.get('fatherMobile');
  const fEmail = group.get('fatherEmail');

  const mName = group.get('motherName');
  const mMob = group.get('motherMobile');
  const mEmail = group.get('motherEmail');

  let hasError = false;

  if ((fMob?.value || fEmail?.value) && !fName?.value) {
    fName?.setErrors({ ...(fName.errors || {}), requiredIfAny: true }, { emitEvent: false });
    hasError = true;
  } else if (fName?.hasError('requiredIfAny')) {
    const errs = { ...fName.errors };
    delete errs['requiredIfAny'];
    fName.setErrors(Object.keys(errs).length ? errs : null, { emitEvent: false });
  }

  if ((mMob?.value || mEmail?.value) && !mName?.value) {
    mName?.setErrors({ ...(mName.errors || {}), requiredIfAny: true }, { emitEvent: false });
    hasError = true;
  } else if (mName?.hasError('requiredIfAny')) {
    const errs = { ...mName.errors };
    delete errs['requiredIfAny'];
    mName.setErrors(Object.keys(errs).length ? errs : null, { emitEvent: false });
  }

  return hasError ? { requiredIfAny: true } : null;
};

@Component({
  selector: 'app-da-parents-details',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    ToastModule,
    FieldErrorComponent
  ],
  providers: [MessageService],
  templateUrl: './parents-details.component.html',
  styleUrl: './parents-details.component.scss'
})
export class DaParentsDetailsComponent implements OnInit {
  @Input() parentForm!: FormGroup;
  @Output() loaded = new EventEmitter<void>();

  // Track database record IDs for updates
  fatherRecordId: number | null = null;
  motherRecordId: number | null = null;
  isSaving: boolean = false;

  constructor(
    private fb: FormBuilder,
    private parentsService: ParentsInformationService,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    // If not already defined, register parentsDetails FormGroup inside parentForm
    if (!this.parentForm.get('parentsDetails')) {
      const group = this.fb.group({
        fatherName: ['', FieldValidators.personNameOptional],
        fatherMobile: ['', FieldValidators.mobile10Optional],
        fatherEmail: ['', FieldValidators.emailOptional],
        motherName: ['', FieldValidators.personNameOptional],
        motherMobile: ['', FieldValidators.mobile10Optional],
        motherEmail: ['', FieldValidators.emailOptional]
      }, { validators: parentValidator });
      this.parentForm.addControl('parentsDetails', group);
    }

    this.loadParentsData();
  }

  loadParentsData() {
    try {
      const raw = localStorage.getItem('account');
      if (raw) {
        const account = JSON.parse(raw);
        const userId = account?.userId;
        if (userId) {
          this.parentsService.getParentsInformationByStudentId(userId).subscribe({
            next: (res: any) => {
              const list = Array.isArray(res) ? res : (res?.result || []);
              if (list && list.length > 0) {
                let fatherData = { name: '', phone: '', email: '' };
                let motherData = { name: '', phone: '', email: '' };

                list.forEach((p: any) => {
                  const relation = p.relation ?? p.Relation;
                  const recordId = p.id ?? p.Id;
                  // Father relation is 1 or 'Father'
                  if (relation === 1 || String(relation).toLowerCase() === 'father') {
                    this.fatherRecordId = recordId;
                    fatherData = {
                       name: p.name || p.Name || '',
                      phone: p.phone || p.Phone || '',
                      email: p.email || p.Email || ''
                    };
                  }
                  // Mother relation is 2 or 'Mother'
                  else if (relation === 2 || String(relation).toLowerCase() === 'mother') {
                    this.motherRecordId = recordId;
                    motherData = {
                         name: p.name || p.Name || '',
                      phone: p.phone || p.Phone || '',
                      email: p.email || p.Email || ''
                    };
                  }
                });

                this.parentForm.get('parentsDetails')?.patchValue({
                  fatherName: fatherData.name,
                  fatherMobile: fatherData.phone,
                  fatherEmail: fatherData.email,
                  motherName: motherData.name,
                  motherMobile: motherData.phone,
                  motherEmail: motherData.email
                });
              }
              this.loaded.emit();
            },
            error: (err) => {
              console.error('Failed to load parents info:', err);
              this.loaded.emit();
            }
          });
        }
      }
    } catch (e) {
      console.error('Error loading parents details:', e);
    }
  }

saveParentInfo() {
  this.parentForm.get('parentsDetails')?.markAllAsTouched();
  if (this.parentForm.get('parentsDetails')?.invalid) {
    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please fill in all required fields correctly.' });
    this.scrollToFirstError();
    return;
  }

  if (!this.parentForm.get('parentsDetails')?.dirty) {
    this.messageService.add({ severity: 'info', summary: 'No Changes', detail: 'No changes detected. Nothing to update.' });
    return;
  }

  let userId = '';
  try {
    const raw = localStorage.getItem('account');
    if (raw) userId = JSON.parse(raw)?.userId || '';
  } catch (e) {}

  if (!userId) {
    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'User ID not found.' });
    return;
  }

  const val = this.parentForm.get('parentsDetails')?.value;
  const group = this.parentsDetailsForm;
  const requests: Observable<any>[] = [];

  // ✅ Check if any father field is dirty
  const fatherDirty = ['fatherName', 'fatherEmail', 'fatherMobile']
    .some(f => group.get(f)?.dirty);

  if (fatherDirty && (val.fatherName || val.fatherEmail || val.fatherMobile)) {
    const fatherPayload = {
      studentId: userId,
      name: val.fatherName || '',
      email: val.fatherEmail || '',
      phone: val.fatherMobile || '',
      relation: 1
    };
    requests.push(
      this.fatherRecordId
        ? this.parentsService.updateParentsInformation(this.fatherRecordId, fatherPayload)
        : this.parentsService.createParentsInformation(fatherPayload)
    );
  }

  // ✅ Check if any mother field is dirty
  const motherDirty = ['motherName', 'motherEmail', 'motherMobile']
    .some(f => group.get(f)?.dirty);

  if (motherDirty && (val.motherName || val.motherEmail || val.motherMobile)) {
    const motherPayload = {
      studentId: userId,
      name: val.motherName || '',
      email: val.motherEmail || '',
      phone: val.motherMobile || '',
      relation: 2
    };
    requests.push(
      this.motherRecordId
        ? this.parentsService.updateParentsInformation(this.motherRecordId, motherPayload)
        : this.parentsService.createParentsInformation(motherPayload)
    );
  }

  if (requests.length === 0) {
    this.messageService.add({ severity: 'info', summary: 'Info', detail: 'No parent information to save.' });
    return;
  }

  this.isSaving = true;
  forkJoin(requests).subscribe({
    next: () => {
      this.isSaving = false;
      this.loadParentsData();
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Parents details saved successfully.' });
      this.parentForm.get('parentsDetails')?.markAsPristine();
    },
    error: (err) => {
      this.isSaving = false;
      this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message || 'Failed to save parents details.' });
    }
  });
}

  get parentsDetailsForm() {
    return this.parentForm.get('parentsDetails') as FormGroup;
  }

  readonly isFieldInvalid = isFieldInvalid;

  private scrollToFirstError() {
    setTimeout(() => {
      const selectors = [
        'input.ng-invalid',
        'textarea.ng-invalid',
        'p-dropdown.ng-invalid',
        'p-calendar.ng-invalid',
        'p-autocomplete.ng-invalid',
        'p-selectbutton.ng-invalid'
      ];
      const el = document.querySelector(selectors.join(', '));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusable = el as HTMLElement;
        if (typeof focusable.focus === 'function') {
          focusable.focus();
        }
      }
    }, 100);
  }
}
